import FeMaleAvatar from '@/assets/images/Female-avatar.png';
import MaleAvatar from '@/assets/images/Male-avatar.png';
import Email from '@/assets/SVGs/icons/Email';
import PhoneIcon from '@/assets/SVGs/icons/PhoneIcon';
import TimeLine from '@/assets/SVGs/icons/TimeLine';
import { endpoints } from '@/services/endpoints';
import {
  axiosGetRequest,
  axiosPatchRequest,
  axiosPostRequest,
  axiosDeleteRequest,
} from '@/services/Request';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import moment from 'moment';

import { DateIcon } from '@/assets/SVGs/icons/DateIcon';
import GenderIcon from '@/assets/SVGs/icons/GenderIcon';
import RoleIcon from '@/assets/SVGs/icons/RoleIcon';
import SingleUserIcon from '@/assets/SVGs/icons/SingleUserIcon';
import { useSearchParams, useNavigate } from 'react-router';
import { Skeleton } from '@/components/ui/Skeleton';
import AppBadge from '@/components/ui/AppBade';
import AppButton from '@/components/ui/AppButton';
import AppInput from '@/components/ui/AppInput';
import AppPhoneInput from '@/components/ui/AppPhoneInput';
import {
  ShieldBan,
  ShieldCheck,
  BookOpen,
  MessageSquare,
  AlertTriangle,
  Activity,
  Pencil,
  X,
  Medal,
  Trash2,
  BellRing,
} from 'lucide-react';
import { SendNotificationModal } from '@/pages/Notifications/Notifications';
import { toast } from 'react-toastify';
import { useState } from 'react';
import { getBookImageUrl, IBookData } from '@/types';

interface UserProfileProps {
  userId?: string;
}

const ROLES = ['user', 'editor', 'admin'] as const;
const BADGES = ['Bronze', 'Silver', 'Gold', 'Platinum', 'Diamond'] as const;

const BADGE_COLORS: Record<string, string> = {
  Bronze: 'bg-amber-700',
  Silver: 'bg-slate-400',
  Gold: 'bg-yellow-400',
  Platinum: 'bg-cyan-300',
  Diamond: 'bg-blue-400',
  A1: 'bg-emerald-500',
  A3: 'bg-purple-500',
};

function getActivityIcon(type: string) {
  switch (type?.toUpperCase()) {
    case 'ADD_BOOK':
      return { Icon: BookOpen, color: 'bg-emerald-500' };
    case 'SEND_MESSAGE':
    case 'CREATE_CHAT':
      return { Icon: MessageSquare, color: 'bg-blue-500' };
    case 'LIKE_BOOK':
      return { Icon: Activity, color: 'bg-rose-500' };
    case 'TICKET':
    case 'REPORT':
      return { Icon: AlertTriangle, color: 'bg-amber-500' };
    default:
      return { Icon: Activity, color: 'bg-coal-400' };
  }
}

export default function UserProfile({ userId }: UserProfileProps) {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const id = searchParams.get('uid');
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState<'activity' | 'books' | 'badges'>('activity');
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showNotifyModal, setShowNotifyModal] = useState(false);
  // Local badge list — seeded from user data, updated optimistically on assign
  const [localBadges, setLocalBadges] = useState<string[]>([]);

  const { data: usersData, isLoading } = useQuery({
    queryKey: [endpoints.getSpecificUser, id, userId],
    queryFn: () => axiosGetRequest(endpoints.getSpecificUser, { id: id || userId }),
    onSuccess: (data: any) => {
      const badges = (data as any)?.result?.badges || (data as any)?.result?.Badge || [];
      setLocalBadges(Array.isArray(badges) ? badges : []);
    },
  } as any);

  const { data: activitiesData, isLoading: activitiesLoading } = useQuery({
    queryKey: [endpoints.getUserActivities, id || userId],
    queryFn: () => axiosGetRequest(endpoints.getUserActivities, { id: id || userId }),
    enabled: !!id || !!userId,
  });

  const { data: userBooksData, isLoading: booksLoading } = useQuery({
    queryKey: [endpoints.getUserBooks, id || userId],
    queryFn: () => axiosGetRequest(endpoints.getUserBooks, { id: id || userId, limit: 20 }),
    enabled: (!!id || !!userId) && activeTab === 'books',
  });

  const toggleBlock = useMutation({
    mutationFn: (data: { isBlocked: boolean }) =>
      axiosPatchRequest(endpoints.adminUpdateUserProfile, data, { id: id || userId || '' }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [endpoints.getSpecificUser, id, userId] });
      toast.success((usersData as any)?.result?.isBlocked ? 'User unblocked' : 'User blocked');
    },
    onError: () => toast.error('Failed to update user'),
  });

  const changeRole = useMutation({
    mutationFn: (role: string) =>
      axiosPatchRequest(endpoints.adminUpdateUserProfile, { role }, { id: id || userId || '' }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [endpoints.getSpecificUser, id, userId] });
      toast.success('Role updated successfully');
    },
    onError: () => toast.error('Failed to change role'),
  });

  const assignBadge = useMutation({
    mutationFn: (Badge: string) =>
      axiosPostRequest(endpoints.assignBadge, { user_id: id || userId, Badge }),
    onSuccess: (response: any) => {
      // The assign endpoint returns the full updated badge list as result
      const updatedBadges = response?.result;
      if (Array.isArray(updatedBadges)) {
        setLocalBadges(updatedBadges);
      }
      // Also invalidate the user query so the DB state stays in sync
      queryClient.invalidateQueries({ queryKey: [endpoints.getSpecificUser, id, userId] });
      toast.success('Badge assigned successfully');
    },
    onError: (err: any) => toast.error(err?.message || 'Failed to assign badge'),
  });

  const deleteUser = useMutation({
    mutationFn: () => axiosDeleteRequest(endpoints.adminDeleteUser, { id: id || userId || '' }),
    onSuccess: () => {
      toast.success('User deleted successfully');
      queryClient.invalidateQueries({ queryKey: [endpoints.getAllUsers] });
      navigate('/users', { replace: true });
    },
    onError: () => toast.error('Failed to delete user'),
  });

  const user = (usersData as any)?.result;
  // Derive displayed badges: prefer local state (updated by assign), fall back to user data
  const displayedBadges: string[] =
    localBadges.length > 0 ? localBadges : user?.badges || user?.Badge || [];

  return (
    <>
      {isLoading ? (
        <UserDetailsSkeleton />
      ) : (
        <div className="space-y-5">
          {/* User profile header */}
          {(userId || (id && !isLoading)) && (
            <div className="rounded-xl border border-coal-200 bg-white p-5 shadow-card">
              <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                <div className="flex items-center gap-4">
                  <img
                    src={user?.gender === 'male' ? MaleAvatar : FeMaleAvatar}
                    className="h-16 w-16 rounded-full"
                    alt="Avatar"
                  />
                  <div>
                    <h2 className="text-xl font-semibold text-coal-900">{user?.fullName}</h2>
                    <div className="mt-1 flex flex-wrap items-center gap-2">
                      <AppBadge size="small" variant="label" color="info">
                        {user?.role}
                      </AppBadge>
                      {user?.isActive ? (
                        <AppBadge size="small" variant="label" color="success">
                          Active
                        </AppBadge>
                      ) : (
                        <AppBadge size="small" variant="label" color="error">
                          Inactive
                        </AppBadge>
                      )}
                      {user?.isBlocked && (
                        <AppBadge size="small" variant="label" color="error">
                          Blocked
                        </AppBadge>
                      )}
                      {user?.isVerified && (
                        <AppBadge size="small" variant="label" color="success">
                          Verified
                        </AppBadge>
                      )}
                    </div>
                  </div>
                </div>

                {/* Admin Actions */}
                {id && (
                  <div className="flex flex-wrap gap-2">
                    {/* Edit User */}
                    <AppButton size="sm" theme="default" onClick={() => setShowEditModal(true)}>
                      <Pencil className="h-4 w-4" />
                      Edit
                    </AppButton>

                    {/* Change Role */}
                    <select
                      className="h-9 rounded-lg border border-coal-300 bg-white px-3 text-sm text-coal-800 focus:border-primary focus:outline-none"
                      value={user?.role || 'user'}
                      onChange={e => changeRole.mutate(e.target.value)}
                    >
                      {ROLES.map(r => (
                        <option key={r} value={r}>
                          {r.charAt(0).toUpperCase() + r.slice(1)}
                        </option>
                      ))}
                    </select>

                    {/* Block / Unblock */}
                    {user?.isBlocked ? (
                      <AppButton
                        theme="success"
                        size="sm"
                        onClick={() => toggleBlock.mutate({ isBlocked: false })}
                        loading={toggleBlock.isPending}
                      >
                        <ShieldCheck className="h-4 w-4" />
                        Unblock
                      </AppButton>
                    ) : (
                      <AppButton
                        theme="error"
                        size="sm"
                        onClick={() => toggleBlock.mutate({ isBlocked: true })}
                        loading={toggleBlock.isPending}
                      >
                        <ShieldBan className="h-4 w-4" />
                        Block
                      </AppButton>
                    )}

                    {/* Send Push Notification */}
                    {id && (
                      <AppButton size="sm" theme="default" onClick={() => setShowNotifyModal(true)}>
                        <BellRing className="h-4 w-4" />
                        Notify
                      </AppButton>
                    )}

                    {/* Delete User — not available for superAdmin */}
                    {user?.role !== 'superAdmin' && id && (
                      <AppButton
                        theme="error"
                        size="sm"
                        onClick={() => setShowDeleteConfirm(true)}
                        loading={deleteUser.isPending}
                      >
                        <Trash2 className="h-4 w-4" />
                        Delete
                      </AppButton>
                    )}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* User About and Tabbed Content */}
          <div className="grid grid-cols-6 gap-5">
            {/* User about */}
            <div className="col-span-6 rounded-xl border border-coal-200 bg-white p-5 shadow-card lg:col-span-2">
              <h2 className="mb-3 text-xs font-semibold uppercase tracking-wider text-coal-400">
                About
              </h2>
              <ul className="space-y-3">
                <li className="flex items-center gap-2.5">
                  <SingleUserIcon height={18} width={18} className="text-coal-400" />
                  <span className="text-sm text-coal-600">
                    Full Name: <span className="font-medium text-coal-800">{user?.fullName}</span>
                  </span>
                </li>
                <li className="flex items-center gap-2.5">
                  <GenderIcon height={18} width={18} className="text-coal-400" />
                  <span className="text-sm text-coal-600">
                    Gender: <span className="font-medium text-coal-800">{user?.gender}</span>
                  </span>
                </li>
                <li className="flex items-center gap-2.5">
                  <DateIcon height={16} width={16} className="text-coal-400" />
                  <span className="text-sm text-coal-600">
                    Birth Date:{' '}
                    <span className="font-medium text-coal-800">
                      {user?.birthDate ? moment(user.birthDate).format('MMMM Do YYYY') : 'Not set'}
                    </span>
                  </span>
                </li>
                <li className="flex items-center gap-2.5">
                  <RoleIcon height={18} width={18} className="text-coal-400" />
                  <span className="text-sm text-coal-600">
                    Role: <span className="font-medium text-coal-800">{user?.role}</span>
                  </span>
                </li>
              </ul>
              <hr className="my-4 border-coal-200" />
              <h2 className="mb-3 text-xs font-semibold uppercase tracking-wider text-coal-400">
                Contacts
              </h2>
              <ul className="space-y-3">
                <li className="flex items-center gap-2.5">
                  <Email />
                  <span className="text-sm text-coal-600">
                    E-mail:{' '}
                    <span className="font-medium text-coal-800">
                      {user?.email || 'Not available'}
                    </span>
                  </span>
                </li>
                <li className="flex items-center gap-2.5">
                  <PhoneIcon />
                  <span className="text-sm text-coal-600">
                    Phone:{' '}
                    <span className="font-medium text-coal-800">
                      {user?.phoneNumber ?? 'Not Available'}
                    </span>
                  </span>
                </li>
              </ul>
              <hr className="my-4 border-coal-200" />
              <h2 className="mb-3 text-xs font-semibold uppercase tracking-wider text-coal-400">
                Account Info
              </h2>
              <ul className="space-y-3">
                <li className="flex items-center gap-2.5">
                  <DateIcon height={16} width={16} className="text-coal-400" />
                  <span className="text-sm text-coal-600">
                    Joined:{' '}
                    <span className="font-medium text-coal-800">
                      {user?.createdAt ? moment(user.createdAt).format('MMM DD, YYYY') : 'N/A'}
                    </span>
                  </span>
                </li>
                <li className="flex items-center gap-2.5">
                  <RoleIcon height={18} width={18} className="text-coal-400" />
                  <span className="text-sm text-coal-600">
                    Provider:{' '}
                    <span className="font-medium text-coal-800">{user?.provider || 'SYSTEM'}</span>
                  </span>
                </li>
              </ul>
            </div>

            {/* Tabs panel */}
            <div className="col-span-6 rounded-xl border border-coal-200 bg-white shadow-card lg:col-span-4">
              {/* Tab bar */}
              <div className="flex border-b border-coal-200">
                {[
                  { key: 'activity', label: 'Activity', Icon: TimeLine },
                  { key: 'books', label: 'Books', Icon: BookOpen },
                  { key: 'badges', label: 'Badges', Icon: Medal },
                ].map(tab => (
                  <button
                    key={tab.key}
                    onClick={() => setActiveTab(tab.key as any)}
                    className={`-mb-px flex items-center gap-1.5 border-b-2 px-4 py-3 text-sm font-medium transition-colors ${
                      activeTab === tab.key
                        ? 'border-primary text-primary'
                        : 'border-transparent text-coal-500 hover:text-coal-700'
                    }`}
                  >
                    {tab.label}
                  </button>
                ))}
              </div>

              <div className="p-5">
                {/* ── Activity Tab ── */}
                {activeTab === 'activity' && (
                  <>
                    <div className="mb-4 flex items-center gap-2">
                      <TimeLine />
                      <h2 className="text-base font-semibold text-coal-900">Activity Timeline</h2>
                    </div>
                    {activitiesLoading ? (
                      <div className="space-y-3">
                        <Skeleton className="h-12 w-full rounded" />
                        <Skeleton className="h-12 w-full rounded" />
                        <Skeleton className="h-12 w-3/4 rounded" />
                      </div>
                    ) : activitiesData?.result && activitiesData.result.length > 0 ? (
                      <div className="relative ml-3 border-l-2 border-coal-200 pl-6">
                        {activitiesData.result.map((activity: any, index: number) => {
                          const activityIcon = getActivityIcon(activity.actionType);
                          return (
                            <div
                              key={activity._id || index}
                              className="group relative pb-6 last:pb-0"
                            >
                              <div
                                className={`absolute -left-[31px] flex h-4 w-4 items-center justify-center rounded-full border-2 border-white ${activityIcon.color}`}
                              >
                                <activityIcon.Icon className="h-2 w-2 text-white" />
                              </div>
                              <div>
                                <p className="text-sm font-medium text-coal-800">
                                  {activity.description ||
                                    activity.meta?.content ||
                                    activity.actionType?.replace(/_/g, ' ') ||
                                    'Activity'}
                                </p>
                                <p className="mt-0.5 text-xs text-coal-400">
                                  {moment(activity.createdAt || activity.timestamp).fromNow()}
                                </p>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    ) : (
                      <p className="text-center text-sm text-coal-400">No activity yet</p>
                    )}
                  </>
                )}

                {/* ── Books Tab ── */}
                {activeTab === 'books' && (
                  <>
                    <h2 className="mb-4 text-base font-semibold text-coal-900">Books Created</h2>
                    {booksLoading ? (
                      <div className="space-y-2">
                        {Array.from({ length: 4 }).map((_, i) => (
                          <Skeleton key={i} className="h-14 w-full rounded" />
                        ))}
                      </div>
                    ) : userBooksData?.result?.length > 0 ? (
                      <div className="divide-y divide-coal-100">
                        {userBooksData.result.map((book: IBookData) => (
                          <div key={book._id} className="flex items-center gap-3 py-3">
                            {book.images?.[0] ? (
                              <img
                                src={getBookImageUrl(book.images[0])}
                                alt={book.title}
                                className="h-10 w-10 flex-shrink-0 rounded-md border border-coal-200 object-cover"
                              />
                            ) : (
                              <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-md bg-coal-100">
                                <BookOpen className="h-4 w-4 text-coal-400" />
                              </div>
                            )}
                            <div className="min-w-0 flex-1">
                              <p className="truncate text-sm font-medium text-coal-800">
                                {book.title}
                              </p>
                              <p className="text-xs capitalize text-coal-400">
                                {book.bookStatus} · {book.city}
                              </p>
                            </div>
                            <AppBadge
                              size="small"
                              variant="label"
                              color={
                                book.bookStatus === 'approved'
                                  ? 'success'
                                  : book.bookStatus === 'pending'
                                    ? 'warning'
                                    : 'error'
                              }
                            >
                              {book.bookStatus}
                            </AppBadge>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-center text-sm text-coal-400">No books created yet</p>
                    )}
                  </>
                )}

                {/* ── Badges Tab ── */}
                {activeTab === 'badges' && (
                  <>
                    <h2 className="mb-4 text-base font-semibold text-coal-900">Badges</h2>

                    {/* Current badges from user data */}
                    {displayedBadges.length > 0 ? (
                      <div className="mb-5 flex flex-wrap gap-2">
                        {displayedBadges.map((badge: string) => (
                          <span
                            key={badge}
                            className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-semibold text-white ${BADGE_COLORS[badge] || 'bg-coal-500'}`}
                          >
                            <Medal className="h-3 w-3" />
                            {badge}
                          </span>
                        ))}
                      </div>
                    ) : (
                      <p className="mb-4 text-sm text-coal-400">No badges yet</p>
                    )}

                    {/* Auto-badge notice */}
                    <p className="mb-3 text-xs text-coal-400">
                      <span className="font-medium">A1</span> and{' '}
                      <span className="font-medium">A3</span> are auto-assigned by the system based
                      on approved books.
                    </p>

                    {/* Assign badge */}
                    {id && (
                      <div>
                        <p className="mb-2 text-xs font-semibold uppercase tracking-wider text-coal-400">
                          Assign Badge
                        </p>
                        <div className="flex flex-wrap gap-2">
                          {BADGES.map(badge => {
                            const alreadyHas = displayedBadges.includes(badge);
                            return (
                              <button
                                key={badge}
                                onClick={() => assignBadge.mutate(badge)}
                                disabled={assignBadge.isPending || alreadyHas}
                                title={alreadyHas ? 'Already assigned' : `Assign ${badge}`}
                                className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs font-semibold transition-all ${
                                  alreadyHas
                                    ? 'cursor-not-allowed border-coal-200 bg-coal-100 text-coal-400'
                                    : `border-coal-300 text-coal-700 hover:border-transparent hover:text-white hover:${BADGE_COLORS[badge]}`
                                }`}
                              >
                                <Medal className="h-3 w-3" />
                                {badge}
                                {alreadyHas && <span className="ml-0.5 text-[10px]">✓</span>}
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Edit User Modal */}
      {showEditModal && user && (
        <EditUserModal
          user={user}
          userId={id || userId || ''}
          onClose={() => setShowEditModal(false)}
          onSuccess={() => {
            queryClient.invalidateQueries({ queryKey: [endpoints.getSpecificUser, id, userId] });
            setShowEditModal(false);
          }}
        />
      )}

      {/* Send Push Notification Modal */}
      {showNotifyModal && user && (
        <SendNotificationModal
          onClose={() => setShowNotifyModal(false)}
          targetUserId={id || userId || ''}
          targetUserName={user.fullName}
        />
      )}
    </>
  );
}

// ─── Edit User Modal ──────────────────────────────────────────────────────────

function EditUserModal({
  user,
  userId,
  onClose,
  onSuccess,
}: {
  user: any;
  userId: string;
  onClose: () => void;
  onSuccess: () => void;
}) {
  const [form, setForm] = useState({
    fullName: user.fullName || '',
    email: user.email || '',
    // phoneNumber: user.phoneNumber || '',
    gender: user.gender || 'male',
    birthDate: user.birthDate ? moment(user.birthDate).format('YYYY-MM-DD') : '',
    isActive: user.isActive ?? true,
    isVerified: user.isVerified ?? false,
    isPhoneNumberVerified: user.isPhoneNumberVerified ?? false,
    isBlocked: user.isBlocked ?? false,
  });

  const updateUser = useMutation({
    mutationFn: (data: any) =>
      axiosPatchRequest(endpoints.adminUpdateUserProfile, data, { id: userId }),
    onSuccess: () => {
      toast.success('User updated successfully');
      onSuccess();
    },
    onError: () => toast.error('Failed to update user'),
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const payload: any = {
      isActive: form.isActive,
      isVerified: form.isVerified,
      isPhoneNumberVerified: form.isPhoneNumberVerified,
      isBlocked: form.isBlocked,
    };
    if (form.fullName) payload.fullName = form.fullName;
    if (form.email) payload.email = form.email;
    if (form.phoneNumber) payload.phoneNumber = form.phoneNumber;
    if (form.gender) payload.gender = form.gender;
    if (form.birthDate) payload.birthDate = form.birthDate;
    updateUser.mutate(payload);
  };

  // Toggle switch component helper
  const ToggleField = ({
    label,
    description,
    field,
  }: {
    label: string;
    description: string;
    field: 'isActive' | 'isVerified' | 'isPhoneNumberVerified' | 'isBlocked';
  }) => (
    <div className="flex items-center justify-between rounded-lg border border-coal-200 px-4 py-3">
      <div>
        <p className="text-sm font-medium text-coal-800">{label}</p>
        <p className="text-xs text-coal-400">{description}</p>
      </div>
      <button
        type="button"
        onClick={() => setForm(p => ({ ...p, [field]: !p[field] }))}
        className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
          form[field] ? 'bg-primary' : 'bg-coal-300'
        }`}
      >
        <span
          className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${
            form[field] ? 'translate-x-6' : 'translate-x-1'
          }`}
        />
      </button>
    </div>
  );

  return (
    <>
      <div className="fixed inset-0 z-50 bg-coal-900/40 backdrop-blur-sm" onClick={onClose} />
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        <div className="relative w-full max-w-lg rounded-xl border border-coal-200 bg-white shadow-dropdown">
          <div className="flex items-center justify-between border-b border-coal-200 px-6 py-4">
            <h3 className="text-lg font-semibold text-coal-900">Edit User</h3>
            <button
              className="rounded-md p-1.5 text-coal-400 hover:bg-coal-100 hover:text-coal-600"
              onClick={onClose}
            >
              <X className="h-4 w-4" />
            </button>
          </div>
          <form onSubmit={handleSubmit} className="max-h-[80vh] overflow-y-auto">
            <div className="flex flex-col gap-4 p-6">
              {/* Personal Info */}
              <p className="text-xs font-semibold uppercase tracking-wider text-coal-400">
                Personal Info
              </p>
              <div>
                <label className="mb-1.5 block text-sm font-medium text-coal-700">Full Name</label>
                <AppInput
                  value={form.fullName}
                  onChange={e => setForm(p => ({ ...p, fullName: e.target.value }))}
                  placeholder="Full name"
                />
              </div>
              <div>
                <label className="mb-1.5 block text-sm font-medium text-coal-700">Email</label>
                <AppInput
                  type="email"
                  value={form.email}
                  onChange={e => setForm(p => ({ ...p, email: e.target.value }))}
                  placeholder="email@example.com"
                />
              </div>
              <div>
                <label className="mb-1.5 block text-sm font-medium text-coal-700">
                  Phone Number
                </label>
                <AppPhoneInput
                  value={form.phoneNumber}
                  onChange={phoneNumber => setForm(prev => ({ ...prev, phoneNumber }))}
                  placeholder="10 9398 4420"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="mb-1.5 block text-sm font-medium text-coal-700">Gender</label>
                  <select
                    value={form.gender}
                    onChange={e => setForm(p => ({ ...p, gender: e.target.value }))}
                    className="h-9 w-full rounded-lg border border-coal-300 bg-white px-3 text-sm text-coal-900 focus:border-primary focus:outline-none"
                  >
                    <option value="male">Male</option>
                    <option value="female">Female</option>
                  </select>
                </div>
                <div>
                  <label className="mb-1.5 block text-sm font-medium text-coal-700">
                    Birth Date
                  </label>
                  <input
                    type="date"
                    value={form.birthDate}
                    onChange={e => setForm(p => ({ ...p, birthDate: e.target.value }))}
                    className="h-9 w-full rounded-lg border border-coal-300 bg-white px-3 text-sm text-coal-900 focus:border-primary focus:outline-none"
                  />
                </div>
              </div>

              {/* Account Status */}
              <p className="mt-2 text-xs font-semibold uppercase tracking-wider text-coal-400">
                Account Status
              </p>
              <ToggleField
                label="Active"
                description="Allow user to log in and use the platform"
                field="isActive"
              />
              <ToggleField
                label="Verified"
                description="Mark phone/email as verified"
                field="isVerified"
              />
              <ToggleField
                label="Phone Number Verified"
                description="Mark the user's phone number as verified"
                field="isPhoneNumberVerified"
              />
              <ToggleField
                label="Blocked"
                description="Prevent user from accessing the platform"
                field="isBlocked"
              />
            </div>
            <div className="flex gap-3 border-t border-coal-200 px-6 py-4">
              <AppButton type="submit" loading={updateUser.isPending} className="flex-1">
                Save Changes
              </AppButton>
              <AppButton type="button" theme="default" onClick={onClose} className="flex-1">
                Cancel
              </AppButton>
            </div>
          </form>
        </div>
      </div>
    </>
  );
}

function UserDetailsSkeleton() {
  return (
    <div className="space-y-5">
      <div className="rounded-xl border border-coal-200 bg-white p-5">
        <div className="flex items-center gap-4">
          <Skeleton className="h-16 w-16 rounded-full" />
          <div className="space-y-2">
            <Skeleton className="h-5 w-40 rounded" />
            <Skeleton className="h-4 w-24 rounded" />
          </div>
        </div>
      </div>
      <div className="grid grid-cols-6 gap-5">
        <div className="col-span-6 rounded-xl border border-coal-200 bg-white p-5 lg:col-span-2">
          <Skeleton className="mb-3 h-4 w-16 rounded" />
          <div className="space-y-2.5">
            <Skeleton className="h-4 w-full rounded" />
            <Skeleton className="h-4 w-3/4 rounded" />
            <Skeleton className="h-4 w-2/3 rounded" />
            <Skeleton className="h-4 w-5/6 rounded" />
          </div>
        </div>
        <div className="col-span-6 rounded-xl border border-coal-200 bg-white p-5 lg:col-span-4">
          <Skeleton className="h-5 w-40 rounded" />
        </div>
      </div>
    </div>
  );
}
